import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, CRED_TYPES, errMsg, PAY_METHODS } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/* ---------- Kunde ---------- */
const EMPTY_CLIENT = { company: "", contact_person: "", email: "", phone: "", website: "", address: "", notes: "" };

export function ClientForm({ open, onOpenChange, client, onSaved }) {
  const isEdit = !!client;
  const [form, setForm] = useState(EMPTY_CLIENT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(client ? { ...EMPTY_CLIENT, ...client } : EMPTY_CLIENT);
  }, [open, client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company.trim()) return toast.error("Firmenname ist erforderlich");
    setSaving(true);
    try {
      const res = isEdit ? await api.put(`/clients/${client.id}`, form) : await api.post("/clients", form);
      toast.success(isEdit ? "Kunde aktualisiert" : "Kunde angelegt");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1D225C]">{isEdit ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Firma / Name *</Label>
            <Input data-testid="client-form-company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="z. B. Müller Sanitär" className="mt-1.5" />
          </div>
          <div>
            <Label>Ansprechpartner</Label>
            <Input data-testid="client-form-contact" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>E-Mail</Label>
            <Input data-testid="client-form-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input data-testid="client-form-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Website</Label>
            <Input data-testid="client-form-website" value={form.website} onChange={(e) => set("website", e.target.value)} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Adresse</Label>
            <Input data-testid="client-form-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Notizen</Label>
            <Textarea data-testid="client-form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" data-testid="client-form-cancel" onClick={() => onOpenChange(false)} className="rounded-xl">Abbrechen</Button>
          <Button data-testid="client-form-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Zahlung ---------- */
export function PaymentForm({ open, onOpenChange, projects = [], defaultProjectId, payment, onSaved }) {
  const isEdit = !!payment;
  const [form, setForm] = useState({ project_id: "", amount: "", date: "", method: "ueberweisung", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (payment) {
      setForm({ project_id: payment.project_id, amount: payment.amount, date: payment.date, method: payment.method, notes: payment.notes || "" });
    } else {
      setForm({ project_id: defaultProjectId || "", amount: "", date: new Date().toISOString().slice(0, 10), method: "ueberweisung", notes: "" });
    }
  }, [open, payment, defaultProjectId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.project_id) return toast.error("Bitte ein Projekt auswählen");
    if (!(parseFloat(form.amount) > 0)) return toast.error("Betrag muss größer als 0 sein");
    if (!form.date) return toast.error("Datum ist erforderlich");
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      const res = isEdit ? await api.put(`/payments/${payment.id}`, payload) : await api.post("/payments", payload);
      toast.success(isEdit ? "Zahlung aktualisiert" : "Zahlung eingetragen");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="payment-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1D225C]">{isEdit ? "Zahlung bearbeiten" : "Zahlung an Mondher eintragen"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Projekt *</Label>
            <Select value={form.project_id} onValueChange={(v) => set("project_id", v)} disabled={isEdit}>
              <SelectTrigger data-testid="payment-form-project" className="mt-1.5"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.client_name ? `${p.client_name} – ` : ""}{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Betrag (€) *</Label>
              <Input data-testid="payment-form-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="400" className="mt-1.5" />
            </div>
            <div>
              <Label>Datum *</Label>
              <Input data-testid="payment-form-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Zahlungsmethode</Label>
            <Select value={form.method} onValueChange={(v) => set("method", v)}>
              <SelectTrigger data-testid="payment-form-method" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PAY_METHODS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notiz</Label>
            <Input data-testid="payment-form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="z. B. 1. Abschlag" className="mt-1.5" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" data-testid="payment-form-cancel" onClick={() => onOpenChange(false)} className="rounded-xl">Abbrechen</Button>
          <Button data-testid="payment-form-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
            {saving ? "Speichern…" : "Zahlung speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Zugangsdaten ---------- */
const EMPTY_CRED = { attach: "project", project_id: "", client_id: "", service_type: "wordpress", label: "", login_url: "", username: "", password: "", notes: "" };

export function CredentialForm({ open, onOpenChange, credential, projects = [], clients = [], defaultProjectId, onSaved }) {
  const isEdit = !!credential;
  const [form, setForm] = useState(EMPTY_CRED);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (credential) {
      setForm({
        attach: credential.project_id ? "project" : "client",
        project_id: credential.project_id || "",
        client_id: credential.client_id || "",
        service_type: credential.service_type || "other",
        label: credential.label || "", login_url: credential.login_url || "",
        username: credential.username || "", password: "", notes: credential.notes || "",
      });
    } else {
      setForm({ ...EMPTY_CRED, project_id: defaultProjectId || "" });
    }
  }, [open, credential, defaultProjectId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.label.trim()) return toast.error("Bezeichnung ist erforderlich");
    if (!isEdit && !form.password) return toast.error("Passwort ist erforderlich");
    setSaving(true);
    const payload = {
      project_id: form.attach === "project" && form.project_id ? form.project_id : null,
      client_id: form.attach === "client" && form.client_id ? form.client_id : null,
      service_type: form.service_type, label: form.label, login_url: form.login_url,
      username: form.username, password: form.password, notes: form.notes,
    };
    try {
      const res = isEdit ? await api.put(`/credentials/${credential.id}`, payload) : await api.post("/credentials", payload);
      toast.success(isEdit ? "Zugang aktualisiert" : "Zugang gespeichert");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="credential-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1D225C]">{isEdit ? "Zugang bearbeiten" : "Neuer Zugang"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Zugeordnet zu</Label>
            <Select value={form.attach} onValueChange={(v) => set("attach", v)}>
              <SelectTrigger data-testid="credential-form-attach" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="project">Projekt</SelectItem>
                <SelectItem value="client">Kunde</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.attach === "project" ? (
            <div>
              <Label>Projekt</Label>
              <Select value={form.project_id} onValueChange={(v) => set("project_id", v)}>
                <SelectTrigger data-testid="credential-form-project" className="mt-1.5"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.client_name ? `${p.client_name} – ` : ""}{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>Kunde</Label>
              <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger data-testid="credential-form-client" className="mt-1.5"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Typ</Label>
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
              <SelectTrigger data-testid="credential-form-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CRED_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bezeichnung *</Label>
            <Input data-testid="credential-form-label" value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="z. B. WordPress Admin" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Login-URL</Label>
            <Input data-testid="credential-form-url" value={form.login_url} onChange={(e) => set("login_url", e.target.value)} placeholder="https://beispiel.de/wp-admin" className="mt-1.5" />
          </div>
          <div>
            <Label>Benutzername / E-Mail</Label>
            <Input data-testid="credential-form-username" value={form.username} onChange={(e) => set("username", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Passwort {isEdit && "(leer = unverändert)"}</Label>
            <Input data-testid="credential-form-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" autoComplete="new-password" />
          </div>
          <div className="sm:col-span-2">
            <Label>Notizen</Label>
            <Textarea data-testid="credential-form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5" />
          </div>
        </div>
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Sensible Zugangsdaten – vertraulich behandeln. Passwörter werden verschlüsselt gespeichert.
        </p>
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="outline" data-testid="credential-form-cancel" onClick={() => onOpenChange(false)} className="rounded-xl">Abbrechen</Button>
          <Button data-testid="credential-form-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Zeiteintrag ---------- */
export function TimeEntryForm({ open, onOpenChange, projects = [], defaultProjectId, entry, onSaved }) {
  const isEdit = !!entry;
  const [form, setForm] = useState({ project_id: "", date: "", start: "", end: "", duration_minutes: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setForm({ project_id: entry.project_id, date: entry.date, start: entry.start || "", end: entry.end || "", duration_minutes: entry.duration_minutes || "", description: entry.description || "" });
    } else {
      setForm({ project_id: defaultProjectId || "", date: new Date().toISOString().slice(0, 10), start: "", end: "", duration_minutes: "", description: "" });
    }
  }, [open, entry, defaultProjectId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.project_id) return toast.error("Bitte ein Projekt auswählen");
    if (!form.date) return toast.error("Datum ist erforderlich");
    setSaving(true);
    const payload = { ...form, duration_minutes: parseInt(form.duration_minutes) || 0 };
    try {
      const res = isEdit ? await api.put(`/time-entries/${entry.id}`, payload) : await api.post("/time-entries", payload);
      toast.success(isEdit ? "Eintrag aktualisiert" : "Zeit erfasst");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="timeentry-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1D225C]">{isEdit ? "Zeiteintrag bearbeiten" : "Zeit erfassen"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Projekt *</Label>
            <Select value={form.project_id} onValueChange={(v) => set("project_id", v)}>
              <SelectTrigger data-testid="timeentry-form-project" className="mt-1.5"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.client_name ? `${p.client_name} – ` : ""}{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Datum *</Label>
            <Input data-testid="timeentry-form-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Von</Label>
              <Input data-testid="timeentry-form-start" type="time" value={form.start} onChange={(e) => set("start", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>Bis</Label>
              <Input data-testid="timeentry-form-end" type="time" value={form.end} onChange={(e) => set("end", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>oder Minuten</Label>
              <Input data-testid="timeentry-form-minutes" type="number" min="0" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} placeholder="90" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Beschreibung</Label>
            <Input data-testid="timeentry-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="z. B. Startseite & Mobile-Optimierung" className="mt-1.5" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" data-testid="timeentry-form-cancel" onClick={() => onOpenChange(false)} className="rounded-xl">Abbrechen</Button>
          <Button data-testid="timeentry-form-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Kalender-Termin ---------- */
export function CalendarEventForm({ open, onOpenChange, projects = [], onSaved }) {
  const [form, setForm] = useState({ title: "", date: "", time: "", type: "meeting", project_id: "none" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: "", date: new Date().toISOString().slice(0, 10), time: "", type: "meeting", project_id: "none" });
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titel ist erforderlich");
    setSaving(true);
    try {
      const payload = { ...form, project_id: form.project_id === "none" ? null : form.project_id };
      const res = await api.post("/calendar/events", payload);
      toast.success("Termin gespeichert");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="calendar-event-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1D225C]">Neuer Termin</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label>Titel *</Label>
            <Input data-testid="calendar-event-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="z. B. Kundentermin Müller" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Datum *</Label>
              <Input data-testid="calendar-event-form-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>Uhrzeit</Label>
              <Input data-testid="calendar-event-form-time" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Art</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="calendar-event-form-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="meeting">Kundentermin</SelectItem>
                <SelectItem value="deadline">Wichtige Frist</SelectItem>
                <SelectItem value="payment">Zahlungstermin</SelectItem>
                <SelectItem value="other">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Projekt (optional)</Label>
            <Select value={form.project_id} onValueChange={(v) => set("project_id", v)}>
              <SelectTrigger data-testid="calendar-event-form-project" className="mt-1.5"><SelectValue placeholder="Kein Projekt" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Kein Projekt</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" data-testid="calendar-event-form-cancel" onClick={() => onOpenChange(false)} className="rounded-xl">Abbrechen</Button>
          <Button data-testid="calendar-event-form-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
